import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { List } from "../../types";

export function useUpdateListOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      newOrderPosition,
    }: {
      listId: string;
      newOrderPosition: number;
    }) => {
      const { error } = await supabase
        .from("lists")
        .update({ order_position: newOrderPosition })
        .eq("id", listId);
      if (error) throw error;
    },

    onMutate: async ({ listId, newOrderPosition }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.lists });
      const prevLists = queryClient.getQueryData<List[]>(queryKeys.lists);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useLists out of its pending state and drop the app's loading gate,
      // and onError's snapshot guard could not put it back.
      queryClient.setQueryData<List[]>(queryKeys.lists, (old) =>
        old
          ? old
              .map((l) =>
                l.id === listId ? { ...l, order_position: newOrderPosition } : l
              )
              .sort((a, b) => a.order_position - b.order_position)
          : old
      );

      return { prevLists };
    },

    onError: (err, _, context) => {
      if (context?.prevLists)
        queryClient.setQueryData<List[]>(queryKeys.lists, context.prevLists);
      Sentry.captureException(err, {
        extra: { mutation: "useUpdateListOrder" },
      });
      toast.error("Failed to reorder list. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lists });
    },
  });
}
